import { Button, IconButton, Tooltip } from "@mui/material";
import { isEmpty, isNil, size } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import Flx from "../../../components/layout/Flx";
import BasicModal from "../../../components/modals/BasicModal";
import Htag from "../../../components/typography/Htag";

import { CloseOutlined, DeleteOutlineOutlined } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import NumberInput from "../../../_src_shared/components/inputs/NumberInput";
import { columnTypesDate } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesNumber } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import Txt from "../../../components/typography/Txt";
import {
  fileDocGroups,
  getDocTypesByGroup,
} from "../../../config/fileDocGroupAndTypes";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";

import RffForm from "../../../components/finalForm/RffForm";
import RffGroup from "../../../components/finalForm/shared/RffGroup";
import RffFileUploadField from "../../../components/finalForm/inputs/RffFileUploadField";
import RffSelectToggleField from "../../../components/finalForm/inputs/RffSelectToggleField";
import RffBooleanCheckbox from "../../../components/finalForm/inputs/RffBooleanCheckbox";

// Lazy load heavy components
const AgGridReact = lazy(() =>
  import("ag-grid-react").then((module) => ({ default: module.AgGridReact }))
);
const AgGridTableWrapper = lazy(
  () => import("../../../components/agGrid/AgGridTableWrapper")
);
const DateInput = lazy(() => import("../../../components/inputs/DateInput"));
const SelectInput = lazy(
  () => import("../../../components/inputs/SelectInput")
);

// Loading fallback component
const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: 100,
    }}
  >
    Loading...
  </div>
);

const LoanSizerUploader = ({ show, onClose }) => {
  const { loading, uploadLoanSizer } = useLoanFilesHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const onModalClose = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const onFormSubmit = useCallback(
    (data) => {
      console.log("Submit", data);
      uploadLoanSizer({
        payload: data,
        loanId: loanDrilldown?._id,
        onSuccessFn: (d) => {
          console.log("Success", d);
          onModalClose();
        },
      });
    },
    [uploadLoanSizer, loanDrilldown?._id, onModalClose]
  );

  return (
    <BasicModal
      allowFullScreen
      title={"Upload Loan Sizer"}
      show={show}
      onClose={onModalClose}
      maxWidth={"sm"}
      bodySx={{
        p: 3,
        ".MuiSelect-select": {
          fontSize: "12px",
        },
      }}
    >
      <RffForm
        footerStartContent={
          <RffBooleanCheckbox
            label={"Extract Sizer Data"}
            name="extractSizerData"
          />
        }
        hideBottomValidationErrors
        onSubmit={onFormSubmit}
        loading={loading}
        initialValues={{}}
      >
        <FormQuestions />
      </RffForm>
    </BasicModal>
  );
};

const FormQuestions = () => {
  return (
    <RffGroup>
      <RffSelectToggleField
        name="sizerType"
        label={"Sizer Type"}
        required
        options={["EV GUC", "EV RTL", "EV DSCR", "Other"]}
      />
      <RffSelectToggleField
        required
        name="sizerStage"
        label={"Sizer Stage"}
        options={[
          "Intake Pricing",
          "Request Lock Pricing",
          "Update Lock Pricing",
          "Final Pricing for Closing",
        ]}
      />

      <RffFileUploadField
        required
        label="Upload Loan Sizer"
        docGroup={"Terms & Sizer"}
        docType={"Sizer"}
      />
    </RffGroup>
  );
};

/**
 * SHARED COMPONENTS
 */

const UploadedFilesContent = ({ files, onClearFiles, onModalClose }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const ref = useRef();
  const modalContentRef = useRef(null);

  const [selectedRows, setSelectedRows] = useState(null);
  const [rowData, setRowData] = useState(files);

  const { loading, uploadLoanDocs } = useLoanFilesHook();
  const dispatch = useDispatch();

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 250,
        cellEditor: "agTextCellEditor",
        editable: true,
      },
      {
        headerName: "Document Group",
        field: "docGroup",
        editable: true,
        width: 220,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          values: fileDocGroups || [],
        },
      },
      {
        headerName: "Document Type",
        field: "docType",
        editable: true,
        width: 220,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: (params) => {
          return {
            values: getDocTypesByGroup(params.data.docGroup || "Appraisal"),
          };
        },
      },
    ],
    []
  );

  const onClearFilesClick = useCallback(() => {
    setSelectedRows(null);
    setRowData(null);
    onClearFiles();
  }, [onClearFiles]);

  const handleUploadFiles = useCallback(() => {
    uploadLoanDocs({
      loanId: loanDrilldown?._id,
      files: rowData,
      onSuccessMsg:
        size(rowData) > 1
          ? `${size(rowData)} files uploaded successfully`
          : "File uploaded successfully",
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        onClearFilesClick();
        onModalClose();
      },
    });
  }, [
    uploadLoanDocs,
    loanDrilldown?._id,
    rowData,
    dispatch,
    onClearFilesClick,
    onModalClose,
  ]);

  const handleRowSelectionChange = useCallback((params) => {
    const selected = params.api.getSelectedRows();
    setSelectedRows(selected);
  }, []);

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;

      // If the user changes the filename
      if (colDef.field === "file_display_name") {
        const originalExtension = data.name.split(".").pop().toLowerCase();
        const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
        data.file_display_name = `${newValueWithoutExt}.${originalExtension}`;

        setRowData((prev) =>
          prev.map((item) => (item.id === data.id ? { ...data } : item))
        );
      }

      // If the user changes docGroup
      if (colDef.field === "docGroup") {
        data.docGroup = newValue;
        if (data.docType) {
          data.docType = "";
        }
        setRowData((prev) =>
          prev.map((item) => (item.id === data.id ? { ...data } : item))
        );
      }

      // If the user changes date values, ensure they're stored as ISO strings
      if (colDef.field === "reportEffectiveDate") {
        if (newValue instanceof Date) {
          data.reportEffectiveDate = newValue.toISOString();
        } else if (typeof newValue === "string" && newValue) {
          if (!newValue.includes("T") || !newValue.endsWith("Z")) {
            const dateObj = new Date(newValue);
            if (!isNaN(dateObj.getTime())) {
              data.reportEffectiveDate = dateObj.toISOString();
            }
          }
        }

        setRowData((prev) =>
          prev.map((item) => (item.id === data.id ? { ...data } : item))
        );
      }

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [setRowData]
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesNumber(),
    };
  }, []);

  useEffect(() => {
    setRowData(files);
  }, [files]);

  if (isEmpty(files)) {
    return null;
  }

  return (
    <Flx flexGrow={1} column gap={1} sx={{ mt: 6 }}>
      <Htag h4 sx={{ fontWeight: 500 }}>
        Added Files ({size(rowData)})
      </Htag>

      <Flx
        column
        sx={{
          overflow: "hidden",
          flexGrow: 1,
          gap: 2,
        }}
      >
        <div ref={modalContentRef}>
          <Suspense fallback={<LoadingFallback />}>
            <AgGridTableWrapper height="100%" simpleTable size={4}>
              <AgGridReact
                ref={ref}
                getRowId={(params) => params.data.id}
                rowData={rowData}
                columnDefs={columnDefs}
                singleClickEdit
                columnTypes={columnTypes}
                onSelectionChanged={handleRowSelectionChange}
                domLayout={"autoHeight"}
                onCellValueChanged={handleCellValueChanged}
                stopEditingWhenCellsLoseFocus
                popupParent={modalContentRef.current}
              />
            </AgGridTableWrapper>
          </Suspense>
        </div>
        <Flx fw jb ac>
          <Flx end flexGrow={1} gap={1}>
            <Button
              onClick={onClearFilesClick}
              variant="outlined"
              size="medium"
            >
              Clear Files
            </Button>
            <Button size="medium" onClick={handleUploadFiles} loading={loading}>
              Save & Upload
            </Button>
          </Flx>
        </Flx>
      </Flx>
    </Flx>
  );
};

const BulkActionsToolbar = ({
  selectedRows,
  onDeselectAllRows,
  onDeleteSelectedRows,
  onMetadataUpdate,
}) => {
  const [bulkDocGroup, setBulkDocGroup] = useState(null);
  const [bulkDocType, setBulkDocType] = useState(null);
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState("");
  const [bulkNumExpirationDays, setBulkNumExpirationDays] = useState(null);

  const onGroupChange = useCallback(
    (group) => {
      setBulkDocGroup(group);
      onMetadataUpdate({ newDocGroup: group });
    },
    [onMetadataUpdate]
  );

  const onTypeChange = useCallback(
    (type) => {
      setBulkDocType(type);
      onMetadataUpdate({ newDocType: type });
    },
    [onMetadataUpdate]
  );

  const onEffectiveDateChange = useCallback(
    (date) => {
      setBulkEffectiveDate(date);
      onMetadataUpdate({ newEffectiveDate: date });
    },
    [onMetadataUpdate]
  );

  const onNumExpirationDaysChange = useCallback(
    (num) => {
      setBulkNumExpirationDays(num);
      onMetadataUpdate({ newGuidelineExpirationDays: num });
    },
    [onMetadataUpdate]
  );

  useEffect(() => {
    if (isEmpty(selectedRows)) {
      setBulkDocGroup(null);
      setBulkDocType(null);
      setBulkEffectiveDate(null);
    }
  }, [selectedRows]);

  if (isEmpty(selectedRows)) {
    return null;
  }

  return (
    <Flx
      fw
      jb
      ac
      sx={{
        background: grey[100],
        position: "sticky",
        top: "-8px",
        left: "0",
        borderRadius: 3,
        zIndex: "1111",
        ".MuiSelect-select, .MuiInputBase-root, .txt": {
          fontSize: "12px",
        },
      }}
    >
      <PseudoColumn center width="52px">
        <Tooltip
          placement="top-start"
          title={`Deselect All`}
          disableInteractive
        >
          <IconButton
            onClick={onDeselectAllRows}
            variant="outlined"
            size="small"
          >
            <CloseOutlined className="thin" />
          </IconButton>
        </Tooltip>
      </PseudoColumn>
      <PseudoColumn sx={{ alignItems: "center", pl: 1.5 }} grow={1} gap={1}>
        <Txt sx={{ lineHeight: "normal" }} bold>
          {`Selected Rows (${size(selectedRows)})`}
        </Txt>
        <Tooltip
          title={
            size(selectedRows) === 1
              ? "Delete File"
              : `Delete ${size(selectedRows)} Files`
          }
          disableInteractive
        >
          <IconButton
            onClick={onDeleteSelectedRows}
            color="error"
            variant="outlined"
            size="small"
          >
            <DeleteOutlineOutlined className="thin" />
          </IconButton>
        </Tooltip>
      </PseudoColumn>
      <PseudoColumn width="220px">
        <Suspense fallback={<LoadingFallback />}>
          <SelectInput
            value={bulkDocGroup}
            placeholder="Document Group"
            options={fileDocGroups}
            onChange={onGroupChange}
          />
        </Suspense>
      </PseudoColumn>
      <PseudoColumn width="220px">
        {isNil(bulkDocGroup) ? (
          <Flx ac sx={{ height: "38px" }}>
            <Txt sx={{ opacity: 0.35, fontStyle: "italic", fontSize: "12px" }}>
              Document Group Required
            </Txt>
          </Flx>
        ) : (
          <Suspense fallback={<LoadingFallback />}>
            <SelectInput
              value={bulkDocType}
              placeholder="Document Type"
              options={getDocTypesByGroup(bulkDocGroup) || []}
              onChange={onTypeChange}
            />
          </Suspense>
        )}
      </PseudoColumn>
      <PseudoColumn width="160px">
        <Suspense fallback={<LoadingFallback />}>
          <DateInput
            value={bulkEffectiveDate}
            placeholder="Effective Date"
            onChange={onEffectiveDateChange}
          />
        </Suspense>
      </PseudoColumn>
      <PseudoColumn center width="140px">
        <Tooltip
          title={
            size(selectedRows) === 1
              ? "Delete File"
              : `Delete ${size(selectedRows)} Files`
          }
          disableInteractive
        >
          <NumberInput
            value={bulkNumExpirationDays}
            placeholder="Exp. Days"
            onChange={onNumExpirationDaysChange}
          />
        </Tooltip>
      </PseudoColumn>
    </Flx>
  );
};

const PseudoColumn = ({ sx, center, grow = 0, gap, width, children }) => (
  <Flx
    center={center}
    flexShrink={0}
    gap={gap}
    flexGrow={grow}
    sx={{
      width: width,
      flexBasis: width,
      px: 1,
      py: 1,
      ...sx,
    }}
  >
    {children}
  </Flx>
);

LoanSizerUploader.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
};

UploadedFilesContent.propTypes = {
  files: PropTypes.array,
  onClearFiles: PropTypes.func,
  onModalClose: PropTypes.func,
};

BulkActionsToolbar.propTypes = {
  selectedRows: PropTypes.array,
  onDeselectAllRows: PropTypes.func,
  onDeleteSelectedRows: PropTypes.func,
  onMetadataUpdate: PropTypes.func,
};

PseudoColumn.propTypes = {
  sx: PropTypes.object,
  center: PropTypes.bool,
  grow: PropTypes.number,
  gap: PropTypes.number,
  width: PropTypes.string,
  children: PropTypes.node,
};

export default LoanSizerUploader;
